// QC modal URL control: click the link to verify, pencil to edit, check to return to the link.
import { useState } from "react";
import { Button, Input, RequiredAsterisk } from "../../components/ui.js";

function PencilIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 20h4.5L19 9.5 14.5 5 4 15.5V20Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 12.5 9.5 17 19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Prefix https:// when the stored value has no scheme, so the click is not a same-origin path. */
export function hrefWithScheme(value: string): string {
  const v = value.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

export function ClickableUrlField({
  label,
  value,
  onChange,
  hrefFor,
  placeholder,
  required,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hrefFor?: (value: string) => string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}): JSX.Element {
  const trimmed = value.trim();
  const [editing, setEditing] = useState(!trimmed);
  const href = (hrefFor ?? hrefWithScheme)(trimmed);
  const showLink = !editing && !!trimmed && !!href;

  return (
    <div className="block space-y-1">
      <span className="text-xs font-medium text-slate-600">
        {label}
        {required ? <RequiredAsterisk /> : null}
      </span>
      {disabled ? (
        trimmed && href ? (
          <a
            className="block break-all text-sm text-brand hover:underline"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {value}
          </a>
        ) : (
          <p className="text-sm text-slate-400">—</p>
        )
      ) : showLink ? (
        <div className="flex items-center gap-1">
          <a
            className="min-w-0 flex-1 break-all text-sm text-brand hover:underline"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {value}
          </a>
          <Button
            type="button"
            variant="ghost"
            className="shrink-0 px-1.5 py-1"
            onClick={() => setEditing(true)}
            aria-label="Edit URL"
          >
            <PencilIcon />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            required={required}
          />
          <Button
            type="button"
            variant="ghost"
            className="shrink-0 px-1.5 py-1"
            onClick={() => {
              if (value.trim()) setEditing(false);
            }}
            aria-label="Done editing"
          >
            <CheckIcon />
          </Button>
        </div>
      )}
    </div>
  );
}
